import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, EmptyState, ErrorText, Field, Input, Select, Spinner, Textarea } from '../../components/ui'
import { Icon, type IconName } from '../../components/ui/icons'
import { formatDateTime } from '../../utils/format'
import type { ClinicalOrder, ClinicalOrderType, ExamFavorite, Service } from '../../types/api'
import { PrintableOrder } from '../../components/print/PrintableOrder'

/**
 * Indicaciones estructuradas de la atención (H4):
 *  (a) receta de medicamentos, con plantilla dedicada y pocos clics — la
 *      lección del workaround del HIS de referencia, donde el médico usaba un
 *      "certificado simple" como receta porque la receta formal era lenta;
 *  (b) solicitud de exámenes con favoritos por médico (un set en dos clics);
 *  (c) orden de kinesiología con diagnóstico kinésico, sesiones y comentarios.
 */

const TYPE_META: Record<ClinicalOrderType, { label: string; icon: IconName }> = {
  PRESCRIPTION: { label: 'Receta', icon: 'prescription' },
  EXAM_REQUEST: { label: 'Solicitud de exámenes', icon: 'lab' },
  KINESIOLOGY: { label: 'Orden de kinesiología', icon: 'rehab' },
}

interface DraftItem {
  name: string
  serviceId?: string | null
  dose?: string
  route?: string
  frequency?: string
  duration?: string
  quantity?: string
}

const EMPTY_MED: DraftItem = { name: '', dose: '', route: 'oral', frequency: '', duration: '', quantity: '' }
const ROUTES = ['oral', 'tópica', 'intramuscular', 'endovenosa', 'subcutánea', 'inhalatoria', 'rectal']

export function ClinicalOrdersSection({ encounterId, readOnly }: { encounterId: string; readOnly: boolean }) {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<ClinicalOrderType | null>(null)
  const [error, setError] = useState('')
  const [printing, setPrinting] = useState<ClinicalOrder | null>(null)

  // Receta
  const [meds, setMeds] = useState<DraftItem[]>([{ ...EMPTY_MED }])
  const [prescriptionNotes, setPrescriptionNotes] = useState('')
  // Exámenes
  const [exams, setExams] = useState<DraftItem[]>([{ name: '' }])
  const [favoriteName, setFavoriteName] = useState('')
  // Kinesiología
  const [kine, setKine] = useState({ kinesicDiagnosis: '', sessions: '10', notes: '' })

  const orders = useQuery({
    queryKey: ['clinical-orders', encounterId],
    queryFn: async () => (await api.get<ClinicalOrder[]>(`/encounters/${encounterId}/clinical-orders`)).data,
  })
  const favorites = useQuery({
    queryKey: ['exam-favorites'],
    queryFn: async () => (await api.get<ExamFavorite[]>('/exam-favorites')).data,
    enabled: !readOnly,
  })
  const services = useQuery({
    queryKey: ['services'],
    queryFn: async () => (await api.get<Service[]>('/catalog/services')).data,
    enabled: !readOnly,
  })

  function resetDrafts() {
    setMeds([{ ...EMPTY_MED }])
    setPrescriptionNotes('')
    setExams([{ name: '' }])
    setKine({ kinesicDiagnosis: '', sessions: '10', notes: '' })
    setTab(null)
  }

  const issue = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await api.post<ClinicalOrder>(`/encounters/${encounterId}/clinical-orders`, payload)).data,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['clinical-orders', encounterId] })
      resetDrafts()
      setError('')
      // Emitir e imprimir es un solo gesto: el documento se abre listo.
      setPrinting(created)
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/encounters/${encounterId}/clinical-orders/${id}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinical-orders', encounterId] }),
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const saveFavorite = useMutation({
    mutationFn: async () =>
      (
        await api.post('/exam-favorites', {
          name: favoriteName,
          items: exams.filter((e) => e.name.trim()).map((e) => ({ name: e.name, serviceId: e.serviceId || null })),
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam-favorites'] })
      setFavoriteName('')
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const validMeds = meds.filter((m) => m.name.trim())
  const validExams = exams.filter((e) => e.name.trim())

  return (
    <div className="space-y-4">
      {orders.isLoading ? (
        <Spinner />
      ) : (orders.data ?? []).length === 0 ? (
        <EmptyState message="Sin indicaciones emitidas en esta atención." />
      ) : (
        <ul className="space-y-2">
          {(orders.data ?? []).map((o) => (
            <li key={o.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-steel-100 p-3">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 font-medium text-ink">
                  <Icon name={TYPE_META[o.type].icon} className="h-4 w-4 text-brand-600" />
                  {TYPE_META[o.type].label}
                  <Badge className="bg-steel-100 font-mono text-steel-700">{o.verificationCode}</Badge>
                </p>
                <p className="text-xs text-steel-500">{formatDateTime(o.issuedAt)}</p>
                <p className="mt-1 text-sm text-steel-700">
                  {o.type === 'KINESIOLOGY'
                    ? `${o.kinesicDiagnosis} · ${o.sessions} sesiones`
                    : o.items.map((i) => i.name).join(' · ')}
                </p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <Button variant="secondary" onClick={() => setPrinting(o)}>
                  <Icon name="print" className="mr-1.5 inline h-4 w-4" />
                  Imprimir
                </Button>
                {!readOnly && (
                  <Button variant="ghost" aria-label="Anular documento" onClick={() => remove.mutate(o.id)}>
                    <Icon name="close" className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(TYPE_META) as ClinicalOrderType[]).map((t) => (
              <Button key={t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(tab === t ? null : t)}>
                <Icon name={TYPE_META[t].icon} className="mr-1.5 inline h-4 w-4" />
                {TYPE_META[t].label}
              </Button>
            ))}
          </div>

          {/* ---- Receta ---- */}
          {tab === 'PRESCRIPTION' && (
            <div className="space-y-3 rounded-lg border border-steel-100 bg-surface p-3">
              {meds.map((m, i) => (
                <div key={i} className="grid gap-2 rounded-lg bg-white p-2 sm:grid-cols-6">
                  <div className="sm:col-span-2">
                    <Field label="Medicamento">
                      <Input value={m.name} placeholder="p. ej. Paracetamol" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                    </Field>
                  </div>
                  <Field label="Dosis">
                    <Input value={m.dose ?? ''} placeholder="500 mg" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, dose: e.target.value } : x)))} />
                  </Field>
                  <Field label="Vía">
                    <Select value={m.route ?? 'oral'} onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, route: e.target.value } : x)))}>
                      {ROUTES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Frecuencia">
                    <Input value={m.frequency ?? ''} placeholder="cada 8 horas" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, frequency: e.target.value } : x)))} />
                  </Field>
                  <div className="flex items-end gap-1">
                    <Field label="Duración">
                      <Input value={m.duration ?? ''} placeholder="por 5 días" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, duration: e.target.value } : x)))} />
                    </Field>
                    {meds.length > 1 && (
                      <Button variant="ghost" className="mb-1" aria-label="Quitar" onClick={() => setMeds(meds.filter((_, j) => j !== i))}>
                        <Icon name="close" className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button variant="secondary" onClick={() => setMeds([...meds, { ...EMPTY_MED }])}>+ Otro medicamento</Button>
              <Field label="Indicaciones generales (opcional)">
                <Textarea rows={2} value={prescriptionNotes} onChange={(e) => setPrescriptionNotes(e.target.value)} />
              </Field>
              <Button
                variant="cta"
                disabled={validMeds.length === 0 || issue.isPending}
                onClick={() => issue.mutate({ type: 'PRESCRIPTION', items: validMeds, notes: prescriptionNotes || null })}
              >
                {issue.isPending ? 'Emitiendo…' : 'Emitir receta e imprimir'}
              </Button>
            </div>
          )}

          {/* ---- Exámenes ---- */}
          {tab === 'EXAM_REQUEST' && (
            <div className="space-y-3 rounded-lg border border-steel-100 bg-surface p-3">
              {(favorites.data ?? []).length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-steel-500">Mis sets frecuentes</p>
                  <div className="flex flex-wrap gap-2">
                    {(favorites.data ?? []).map((f) => (
                      <Button key={f.id} variant="secondary" onClick={() => setExams(f.items.map((i) => ({ name: i.name, serviceId: i.serviceId ?? null })))}>
                        <Icon name="favorite" className="mr-1.5 inline h-4 w-4 text-amber-500" />
                        {f.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {exams.map((x, i) => (
                <div key={i} className="grid gap-2 rounded-lg bg-white p-2 sm:grid-cols-[2fr_1.2fr_auto]">
                  <Field label="Examen">
                    <Input value={x.name} placeholder="p. ej. Resonancia magnética de rodilla" onChange={(e) => setExams(exams.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)))} />
                  </Field>
                  <Field label="Prestación del catálogo (opcional)">
                    <Select value={x.serviceId ?? ''} onChange={(e) => setExams(exams.map((y, j) => (j === i ? { ...y, serviceId: e.target.value || null } : y)))}>
                      <option value="">No es una prestación nuestra</option>
                      {(services.data ?? [])
                        .filter((s) => ['IMAGING', 'LABORATORY', 'PROCEDURE'].includes(s.category))
                        .map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Select>
                  </Field>
                  {exams.length > 1 && (
                    <Button variant="ghost" className="mb-1 self-end" aria-label="Quitar" onClick={() => setExams(exams.filter((_, j) => j !== i))}>
                      <Icon name="close" className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="secondary" onClick={() => setExams([...exams, { name: '' }])}>+ Otro examen</Button>

              <div className="flex flex-wrap items-end gap-2 border-t border-steel-100 pt-3">
                <Field label="Guardar esta lista como set frecuente">
                  <Input value={favoriteName} placeholder="p. ej. Estudio de rodilla" onChange={(e) => setFavoriteName(e.target.value)} />
                </Field>
                <Button
                  variant="secondary"
                  className="mb-1"
                  disabled={!favoriteName || validExams.length === 0 || saveFavorite.isPending}
                  onClick={() => saveFavorite.mutate()}
                >
                  Guardar set
                </Button>
              </div>

              <Button
                variant="cta"
                disabled={validExams.length === 0 || issue.isPending}
                onClick={() => issue.mutate({ type: 'EXAM_REQUEST', items: validExams })}
              >
                {issue.isPending ? 'Emitiendo…' : 'Emitir solicitud e imprimir'}
              </Button>
            </div>
          )}

          {/* ---- Kinesiología ---- */}
          {tab === 'KINESIOLOGY' && (
            <div className="space-y-3 rounded-lg border border-steel-100 bg-surface p-3">
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
                <Field label="Diagnóstico kinésico">
                  <Input
                    value={kine.kinesicDiagnosis}
                    placeholder="p. ej. Déficit de fuerza de cuádriceps post reconstrucción de LCA"
                    onChange={(e) => setKine({ ...kine, kinesicDiagnosis: e.target.value })}
                  />
                </Field>
                <Field label="N° de sesiones">
                  <Input type="number" min={1} max={60} value={kine.sessions} onChange={(e) => setKine({ ...kine, sessions: e.target.value })} />
                </Field>
              </div>
              <Field label="Comentarios para el kinesiólogo">
                <Textarea rows={3} value={kine.notes} onChange={(e) => setKine({ ...kine, notes: e.target.value })} />
              </Field>
              <Button
                variant="cta"
                disabled={!kine.kinesicDiagnosis || !Number(kine.sessions) || issue.isPending}
                onClick={() =>
                  issue.mutate({
                    type: 'KINESIOLOGY',
                    kinesicDiagnosis: kine.kinesicDiagnosis,
                    sessions: Number(kine.sessions),
                    notes: kine.notes || null,
                  })
                }
              >
                {issue.isPending ? 'Emitiendo…' : 'Emitir orden e imprimir'}
              </Button>
            </div>
          )}
        </>
      )}

      <ErrorText>{error}</ErrorText>
      {printing && <PrintableOrder order={printing} onClose={() => setPrinting(null)} />}
    </div>
  )
}
